/**
 * GateLoader — loading + error presentational states for the release-features
 * bootstrap.
 *
 * Shown while the once-at-boot `GET /api/v1/release-features` fetch is in flight
 * and (fail-closed) when it errors. The error state does NOT enable any feature
 * — it surfaces an operator-visible message while the resolved set stays empty
 * (SPEC-FEATURE-TOGGLES-RULE-006). An SPA renders `<ReleaseFeaturesGate>` around
 * its router so gated routes/nav are never rendered until the bootstrap
 * resolves or fails closed.
 *
 * All user-facing strings use Lingui macros and ship Hungarian entries in
 * web/src/i18n/locales/hu/messages.po (SPEC-FEATURE-TOGGLES-RULE-011).
 *
 * @module feature-gate/GateLoader
 * @see SPEC-FEATURE-TOGGLES
 */

import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { Trans } from "@lingui/react/macro";
import { bootstrapReleaseFeatures } from "./releaseFeatures";
import { useReleaseFeaturesStatus } from "./useFeature";

// ─── Loading state ───────────────────────────────────────────────────────

/** Centered spinner shown while the release-features bootstrap is in flight. */
export const GateLoading: React.FC = () => (
  <Box
    role="status"
    aria-live="polite"
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap={2}
    py={8}
    minHeight="60vh"
  >
    <CircularProgress />
    <Typography variant="body2" color="text.secondary">
      <Trans>Loading available features…</Trans>
    </Typography>
  </Box>
);

// ─── Error state ─────────────────────────────────────────────────────────

/**
 * Operator-visible error shown when the bootstrap fails. Fail-closed: every
 * gated feature stays hidden; this only surfaces that release state could not
 * be loaded.
 */
export const GateError: React.FC = () => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    px={3}
    py={8}
    minHeight="60vh"
  >
    <Alert severity="warning" sx={{ maxWidth: 560 }}>
      <AlertTitle>
        <Trans>Feature configuration unavailable</Trans>
      </AlertTitle>
      <Trans>
        The list of available features could not be loaded, so optional features are hidden. Please
        refresh the page or contact your administrator if this persists.
      </Trans>
    </Alert>
  </Box>
);

// ─── Bootstrap gate ────────────────────────────────────────────────────────

export interface ReleaseFeaturesGateProps {
  children: React.ReactNode;
}

/**
 * Blocks rendering of `children` (the SPA's gated router/nav) until the
 * release-features bootstrap resolves. Triggers the one-shot fetch on mount,
 * shows `<GateLoading>` while loading, `<GateError>` on failure (fail-closed,
 * empty enabled set), and renders children once ready.
 *
 * NOTE: even in the error case the enabled set is empty, so wrapping children
 * after an error is also safe — but the explicit error surface is the operator
 * signal required by RULE-006. The per-SPA tasks decide whether to render the
 * app shell behind the error (with everything gated off) or to show this state;
 * the default here is to show the error.
 */
export const ReleaseFeaturesGate: React.FC<ReleaseFeaturesGateProps> = ({
  children,
}) => {
  const status = useReleaseFeaturesStatus();

  useEffect(() => {
    // Idempotent: shares a single in-flight fetch across SPAs/mounts.
    void bootstrapReleaseFeatures();
  }, []);

  if (status === "idle" || status === "loading") return <GateLoading />;
  if (status === "error") return <GateError />;
  return <>{children}</>;
};

export default ReleaseFeaturesGate;
